import { getEasedStaggerDelayForTransitionIn, getStaggerDelayMs } from './geatEasedStaggerDelay'
import { sortByVisual } from './sortByVisual'
import { SCOPES } from '../../scope/scope'

// Determines whether a UI target belongs to a given scope. The cascade
// must NOT enumerate the close-icon (or any overlay-owned UI) when it is
// firing for the NORMAL_PAGE scope, and vice versa — otherwise the close
// icon fades in once for the page cascade, then again for the overlay
// cascade. The selector is global by design (site title etc. live in
// body, not inside .lay-content), so we filter ownership here instead
// of by querying a scoped root.
function targetBelongsToScope(target, scope) {
	const isOverlayUi = (target.matches && target.matches('.close-projects-overlay'))
		|| (target.closest && target.closest('#lay-project-overlay'))
	const isPageOverlayUi = target.closest && target.closest('.lay-overlay.lay-show-overlay')
	if (scope === SCOPES.PROJECT_OVERLAY) return !!isOverlayUi
	if (scope === SCOPES.PAGE_OVERLAY) return !!isPageOverlayUi
	// NORMAL_PAGE (or unspecified): everything that is NOT overlay-owned.
	if (isOverlayUi || isPageOverlayUi) return false
	return true
}

function isPillNav(target) {
	return target.matches && target.matches('.nav-style-pill, .mobile-menu-style-pill_menu_style')
}

function belongsToPillNav(target) {
	return target.closest && target.closest('.nav-style-pill, .mobile-menu-style-pill_menu_style')
}

// Mirrors the PHP `lay-hide-sitetitle-menubar-menu-etc` selector — adding
// `.laynav li:not(.sub-menu li)` so the stagger path can fade each top-level
// nav item individually (sub-menu items excluded; they have their own
// visibility behaviour). Used by both the show-stylesheet trigger and
// getUiFadeTargets.
const UI_FADE_SELECTOR =
	'.sitetitle, .overlay-burger, .laynav, .laynav li:not(.sub-menu li), #lay_canvas, .lay-navbar, .lay-fadein, .mobile-title, .burger-wrap, body #fp-nav, body .fp-slidesNav, .mobile-menu-style-style_desktop_menu, .mobile-menu-style-button_menu_style, .lay-background-video, .lay-background-image, .cover-down-arrow-desktop, .cover-down-arrow-phone, .close-projects-overlay'

function cleanupFadeStyles() {
	let style_to_delete = document.getElementById('lay-hide-sitetitle-menubar-menu-etc')
	if (style_to_delete) {
		style_to_delete.remove()
	}

	style_to_delete = document.getElementById('lay-show-sitetitle-menubar-menu-etc')
	if (style_to_delete) {
		style_to_delete.remove()
	}
}

export function getUiFadeTargets(scope) {
	const targets = Array.from(document.querySelectorAll(UI_FADE_SELECTOR))
	return Array.from(new Set(targets)).filter((target) => {
		if (!(target instanceof HTMLElement)) {
			return false
		}
		// Scope filter — see `targetBelongsToScope`. When the caller
		// doesn't pass a scope, the legacy global-enumeration behaviour
		// is preserved (everything is "in scope").
		if (scope && !targetBelongsToScope(target, scope)) return false
		const cs = window.getComputedStyle(target)
		if (cs.display === 'none' || cs.visibility === 'hidden') return false
		// Belt-and-suspenders: even with the scope filter above, the
		// already-locked close icon should never be re-enumerated by
		// a later page cascade.
		if (target.matches && target.matches('.close-projects-overlay.po-fadein-close-icon')) return false
		// `display: none` on an ancestor doesn't show up in the element's
		// own computed style — but `getBoundingClientRect` collapses to
		// 0×0 when any ancestor hides the subtree. This is the cheap test
		// for the common case: `.laynav li` duplicates inside a hidden
		// mobile-menu container on desktop (and vice versa on phone)
		// would otherwise consume cascade slots without ever being
		// visible, pushing the visible site title / menu items hundreds
		// of ms back into the timeline.
		const r = target.getBoundingClientRect()
		if (r.width === 0 && r.height === 0) return false
		// `visibility: hidden` on an ancestor DOES leave a bbox (the
		// element still reserves layout space), so the bbox check above
		// misses it. Walk the ancestor chain to catch this rarer form
		// of "rendered but invisible". Stops at <body> — we trust the
		// document root is visible.
		let p = target.parentElement
		while (p && p !== document.body) {
			if (window.getComputedStyle(p).visibility === 'hidden') return false
			p = p.parentElement
		}
		return true
	})
}

/**
 * @param {number} [contentStaggerColumnCount=0]  Anzahl der Spalten in der Navigation-Stagger-In-Animation (colsInViewport.length), damit Header/UI dieselbe Stagger-Kurve wie der Content nutzt.
 * @param {HTMLElement[]|null} [uiTargetsPreset=null]  Optional: gleiche DOM-Liste wie beim Aufbau des Timelines (vermeidet Abweichung wenn sich Sichtbarkeit bis timeline.begin ändert).
 * @param {string} [scope=null]  Optional scope token (`SCOPES.NORMAL_PAGE` / `SCOPES.PROJECT_OVERLAY` / `SCOPES.PAGE_OVERLAY`). When supplied, the cascade enumerates only UI that belongs to that scope.
 */
var fadeIn_SiteTitle_MenuBar_Menu_etc = function (contentStaggerColumnCount = 0, uiTargetsPreset = null, scope = null) {
	// Dev-mode: one event for the whole cascade kicking off, plus a
	// per-target event below. With scope filtering in place, a page
	// cascade and an overlay cascade should enumerate disjoint targets
	// — if `page=N overlay=M` ever shows both N>0 and M>0 in the same
	// log line, scope filtering is bypassed somewhere (regression).
	if (typeof window.layDevModeEmit === 'function') {
		try {
			const sampleTarget = (Array.isArray(uiTargetsPreset) && uiTargetsPreset[0]) || document.body
			const owners = { overlay: 0, page: 0 }
			const targetsList = Array.isArray(uiTargetsPreset) ? uiTargetsPreset : getUiFadeTargets(scope)
			targetsList.forEach((t) => {
				if (t.matches && (t.matches('.close-projects-overlay') || t.closest('#lay-project-overlay'))) owners.overlay += 1
				else owners.page += 1
			})
			window.layDevModeEmit('ui_cascade:invoked', sampleTarget, 'scope=' + (scope || 'unscoped') + ' targets=' + targetsList.length + ' page=' + owners.page + ' overlay=' + owners.overlay)
		} catch (_) {}
	}
	// first overwrite style that hides things (in frontend.php):
	// '<style id="lay-hide-sitetitle-menubar-menu-etc">
	// then just remove both styles after things have faded in, cause we dont really need them after fadein

	// .lay_canvas just adding this class so ppl can use it on their custom html elements
	const transitionType = layData.navigation_transition_in || 'none'
	const duration = parseInt(layData.navigation_transition_in_duration, 10) || 0

	if (transitionType.indexOf('stagger') !== -1) {
		const rawTargets = Array.isArray(uiTargetsPreset) ? uiTargetsPreset : getUiFadeTargets(scope)
		if (rawTargets.length === 0) {
			cleanupFadeStyles()
			return
		}

		if (!Array.isArray(uiTargetsPreset) && rawTargets.length > 1) {
			sortByVisual(rawTargets, transitionType)
		}

		// Split: normal `.laynav` containers themselves get instantly shown
		// so their top-level lis are visible while each li stagger-fades in
		// individually. Pill navs are different: the moving pill background
		// belongs to the container, so the whole nav fades as one unit and
		// its lis are skipped to avoid a compounded parent+child fade.
		const targets = []
		const laynavContainers = []
		rawTargets.forEach((t) => {
			if (t.matches && t.matches('.laynav')) {
				if (isPillNav(t)) {
					targets.push(t)
					return
				}
				laynavContainers.push(t)
				return
			}
			if (t.matches && t.matches('.laynav li:not(.sub-menu li)') && belongsToPillNav(t)) {
				return
			}
			targets.push(t)
		})

		laynavContainers.forEach((nav) => {
			nav.style.setProperty('transition', 'none', 'important')
			nav.style.setProperty('opacity', '1', 'important')
		})

		const contentCount = Math.max(0, parseInt(contentStaggerColumnCount, 10) || 0)
		const totalStaggerLength = targets.length + contentCount

		// CSS transitions instead of anime.js per-frame inline-opacity updates:
		// under the initial-page-reveal main-thread load anime.js misses frames
		// and the menu/header fade looks janky; CSS transitions are composited.
		//
		// Inline `!important` on transition-delay is required to beat the PHP
		// hide stylesheet's `transition: opacity ${dur}ms ease!important`
		// shorthand (which resets transition-delay to 0 with !important). Only
		// inline-!important wins over stylesheet-!important.
		let maxDelay = 0
		// Independent stagger context for UI targets: site title, menu
		// items, etc. cascade among THEMSELVES, not concatenated with
		// the col cascade. Both contexts start at t=0 in parallel —
		// the user sees the header sweep in at the same time the main
		// content sweeps in, instead of header-first-then-cols.
		// Stagger size still comes from the shared Lay Options field
		// so the rhythm of both contexts feels consistent.
		let navInStagger = parseInt(layData.navigation_transition_in_stagger, 10)
		if (!isFinite(navInStagger) || navInStagger < 0) navInStagger = 100
		const navInEasing = layData.navigation_transition_in_easing || 'easeOutSine'
		targets.forEach((target, index) => {
			const delay = getStaggerDelayMs(index, targets.length, navInStagger, navInEasing)
			if (delay > maxDelay) maxDelay = delay
			target.style.setProperty('transition-delay', delay + 'ms', 'important')
			if (typeof window.layDevModeEmit === 'function') {
				window.layDevModeEmit('ui_cascade:target_armed', target, 'idx=' + index + ' delay=' + Math.round(delay) + 'ms')
			}
		})

		jQuery('head').append('<style id="lay-show-sitetitle-menubar-menu-etc">' + UI_FADE_SELECTOR + '{' + 'opacity: 1;' + '}' + '</style>')

		setTimeout(function () {
			targets.forEach((target) => {
				target.style.removeProperty('transition-delay')
			})
			laynavContainers.forEach((nav) => {
				nav.style.removeProperty('transition')
				nav.style.removeProperty('opacity')
			})
			// Lock close-icon visible BEFORE the show-stylesheet goes
			// away. `.close-projects-overlay` has `opacity: 0` + a
			// `transition: opacity 200ms` baseline in projects_overlays.scss,
			// so removing the show-stylesheet without the lock class
			// already in place lets the icon fade BACK to 0 — then the
			// projects_overlays.js `transition_navigation_in_completed`
			// handler re-adds the lock class ~200ms later, producing the
			// visible double-fade-in the user reported. Locking here
			// bridges the gap with no flicker; the projects_overlays.js
			// handler is now idempotent (the class is just already set).
			targets.forEach((target) => {
				if (target.matches && target.matches('.close-projects-overlay')) {
					target.classList.add('po-fadein-close-icon')
				}
			})
			cleanupFadeStyles()
		}, duration + maxDelay + 50)
		return
	}

	jQuery('head').append('<style id="lay-show-sitetitle-menubar-menu-etc">' + UI_FADE_SELECTOR + '{' + 'opacity: 1;' + '}' + '</style>')
	setTimeout(function () {
		// Same lock-before-cleanup bridge as the stagger path above —
		// the non-stagger path also goes through `cleanupFadeStyles`
		// and so would also flicker the close-icon without this.
		document.querySelectorAll('.close-projects-overlay').forEach((el) => {
			el.classList.add('po-fadein-close-icon')
		})
		cleanupFadeStyles()
	}, duration)
}

export { fadeIn_SiteTitle_MenuBar_Menu_etc }
